// Os tipos do documento do HQ-Lab, formato 2.
//
// A DEFINIÇÃO CANÔNICA VIVE NO BACKEND: `backend-hq-lab/src/documento/esquema.ts`. Lá também
// moram o validador e a migração do formato 1. Aqui é uma cópia só de tipos — o front precisa
// deles para compilar, e não dá para importar através de dois repositórios separados.
//
// Se você mudar um dos dois, mude o outro. O que impede a divergência de passar despercebida é
// o backend: ele valida todo documento que recebe, então um front fora de sincronia começa a
// levar recusa com o caminho do campo errado no corpo da resposta.

export const FORMATO_DOCUMENTO = "hq-lab/documento";
export const VERSAO_DOCUMENTO = 2;

export type FormatoPagina =
  | "tirinha"
  | "pagina"
  | "grade-livre"
  | "webtoon"
  | "capa"
  | "didatica";

export type MotorCanvas = "konva" | "grade-css" | "fluxo-vertical" | "svg";

export interface Caixa {
  x: number;
  y: number;
  largura: number;
  altura: number;
  rotacao?: number;
}

interface ElementoBase extends Caixa {
  id: string;
  nome?: string;
  visivel?: boolean;
  bloqueado?: boolean;
}

export interface Quadro extends ElementoBase {
  tipo: "quadro";
  traco?: number;
  corTraco?: string;
  corFundo?: string;
  raio?: number;
}

export interface Imagem extends ElementoBase {
  tipo: "imagem";
  refImagem: string;
  promptOrigem?: string;
  descricaoAcessivel?: string;
}

export interface Balao extends ElementoBase {
  tipo: "balao";
  texto: string;
  estilo: "fala" | "pensamento" | "grito" | "narracao" | "sussurro";
  anguloRabicho?: number;
  /** O balão acompanha o texto em vez de cortá-lo. É o T1.6 do pedido. */
  crescePorTexto?: boolean;
  fonte?: string;
  tamanhoFonte?: number;
  corTexto?: string;
}

export interface Texto extends ElementoBase {
  tipo: "texto";
  texto: string;
  fonte?: string;
  tamanhoFonte?: number;
  cor?: string;
  peso?: number;
  alinhamento?: "esquerda" | "centro" | "direita";
}

export interface Forma extends ElementoBase {
  tipo: "forma";
  forma: "retangulo" | "elipse" | "linha" | "estrela" | "seta";
  corPreenchimento?: string;
  corTraco?: string;
  traco?: number;
}

export interface Personagem extends ElementoBase {
  tipo: "personagem";
  personagem: "artista" | "pesquisadora" | "professora" | "inventor";
  pose?: string;
  expressao?: string;
  espelhado?: boolean;
}

export type Elemento = Quadro | Imagem | Balao | Texto | Forma | Personagem;

export interface Camada {
  id: string;
  nome: string;
  visivel?: boolean;
  bloqueada?: boolean;
  opacidade?: number;
  elementos: Elemento[];
}

/**
 * A RETICULA — T2.108, e e o unico ponto dos 109 em que ele usa a palavra "essencial".
 *
 * "tem que ter uma forma de APLICAR RETICULA EM TODAS AS PAGINAS, se quiser, para tentar criar
 *  uma UNIFORMIDADE DOS DESENHOS, DO TRACO, etc. Acho que uma feature para aplicacao de reticula
 *  em qualquer um dos campos que a gente vai ter e ESSENCIAL para a ferramenta."
 *
 * A razao que ele da e a que decide o desenho disto: uniformidade. Quando cada imagem gerada sai
 * com um tratamento diferente — e o PL-T2.9 mostra que saem —, a reticula por cima e o que faz a
 * pagina parecer uma coisa so. E conserto de SINTOMA de um defeito que o PL-T2.9 ataca na raiz, e
 * os dois valem.
 *
 * E ELA E DA MARCA, o que eu descobri medindo e nao supondo: a capa oficial de YouTube
 * (`especificacao/marca/feed/capayt.jpg`) usa halftone e linhas de velocidade como textura de
 * fundo. O produto ja tinha `.hq-trama` desde o T1; agora ela tem origem documentada, e a
 * apuracao esta no `CATALOGO-SOCIAL-MEDIA.md`.
 *
 * TRES INTENSIDADES E NAO UM BOOLEANO, porque "se quiser" implica escolha e nao interruptor: uma
 * tirinha de humor aguenta reticula forte; uma pagina didatica com muito texto fica ilegivel com
 * ela. Quem decide e quem monta.
 */
export const RETICULAS = ["nenhuma", "leve", "media", "forte"] as const;
export type Reticula = (typeof RETICULAS)[number];

export const ROTULO_RETICULA: Record<Reticula, string> = {
  nenhuma: "Sem retícula",
  leve: "Leve",
  media: "Média",
  forte: "Forte",
};

/** A opacidade de cada intensidade. Fora do CSS de propósito: o motor SVG também a usa. */
export const OPACIDADE_RETICULA: Record<Reticula, number> = {
  nenhuma: 0,
  leve: 0.18,
  media: 0.34,
  forte: 0.55,
};

/**
 * A GEOMETRIA DO PONTO, e ela fica aqui pela mesma razão que a opacidade.
 *
 * Dois motores desenham a retícula, e por caminhos diferentes: `grade-css` e `fluxo-vertical` com
 * um `radial-gradient` de CSS, e o `svg` com um `<pattern>` de verdade. Com os números escritos em
 * cada um, eles divergiriam no primeiro ajuste — e a divergência seria invisível até alguém
 * comparar duas páginas lado a lado.
 *
 * É a mesma lição que este ticket já pagou três vezes hoje (a lista de fontes duplicada, o rodapé
 * do XequeMath, o prefixo de provedor copiado): **valor que serve a dois lugares não mora dentro
 * de um deles.**
 *
 * `PASSO` é a distância entre dois pontos; `RAIO` é o ponto. Em pixels da folha, que é o sistema
 * em que o SVG desenha — o CSS usa os mesmos números porque a folha do `grade-css` escala por
 * `transform`, e não redesenhando.
 */
export const TRAMA = { PASSO: 9, RAIO: 1.3 } as const;

export interface Pagina {
  id: string;
  formato: FormatoPagina;
  motor: MotorCanvas;
  largura: number;
  altura: number;
  corFundo?: string;
  legenda?: string;
  /**
   * A trama de meio-tom sobre a página inteira (T2.108). Ausente conta como "nenhuma" — toda
   * página publicada antes de hoje não tem o campo, e presumir retícula mudaria o desenho de
   * quem nunca pediu.
   */
  reticula?: Reticula;
  camadas: Camada[];
}

export type Categoria = "didatico" | "narrativa" | "livre";

export interface Serie {
  id: string;
  titulo: string;
  capitulo: number;
  resumo?: string;
}

export interface Publicacao {
  categoria: Categoria;
  /**
   * O EIXO DA PUBLICACAO. "rascunho" quer dizer NAO PUBLICADO.
   *
   * O nome vem de antes do T2 e fica: trocar por "nao-publicado" mudaria o valor gravado em todo
   * documento que ja existe, e as regras do Firestore afirmam este enum em tres lugares. Rotulo
   * de tela nao precisa herdar nome de campo.
   */
  /**
   * `publicado-validacao` existe para NAO ser `publicado`. Uma conta de validacao grava este
   * estado, e com isso o quadrinho dela nao aparece na biblioteca, nao aparece na abertura e nao
   * e legivel por ninguem — as tres portas ja filtravam por `publicado`. Ver
   * `src/validacao/a-conta-de-validacao.ts`.
   */
  estado: "rascunho" | "publicado" | "publicado-validacao";
  /**
   * O SEGUNDO EIXO — T2.61, e ele e INDEPENDENTE do primeiro.
   *
   * "sao duas questoes (...) ou voce publica ou nao publica. E essa questao do finalizado, se
   *  voce finalizar, ta encerrado, e o trabalho final, nao quero editar mais, e vai dessa forma,
   *  SEJA PUBLICADO OU NAO. Ou se ainda esta em andamento."
   *
   * "Seja publicado ou nao" e a frase que torna as quatro combinacoes obrigatorias. Um eixo so
   * com quatro valores nao serviria: ele misturaria "quem ve" com "ainda muda", e a biblioteca
   * precisa filtrar pelos dois separado (T2.60).
   *
   * AUSENTE conta como "em andamento", e isso e de proposito: todo documento publicado antes de
   * hoje nao tem o campo, e presumir "finalizado" trancaria a edicao de gente que nunca pediu
   * para encerrar nada.
   */
  finalizado?: boolean;
  serie?: Serie;
  tags?: string[];
  faixaEtaria?: string;
  /**
   * A INSTITUICAO A QUE ESTE QUADRINHO PERTENCE, quando pertence a uma (T2.63).
   *
   * "a gente poder associar os quadrinhos a determinadas instituicoes de ensino. Entao, para que
   *  aqueles quadrinhos (...) eles podem ate aparecer UM PREVIEW deles e tal, mas eles SO VAO SER
   *  VISIVEIS para um profissional, um educador daquela, educador ou aluno daquela instituicao."
   *
   * AUSENTE significa catalogo aberto, e isso e o padrao. O T2.79 e explicito sobre a escola nao
   * virar portao: "o aluno em teoria poderia entrar independentemente. ELE SO NAO VAI SER
   * ASSOCIADO AQUELA BIBLIOTECA ESPECIFICA." Ou seja, isto e um FILTRO de biblioteca, nao um
   * portao de plataforma — e o catalogo publico continua existindo do lado.
   *
   * ── POR QUE ISTO SE ENCAIXA SEM VIOLENCIA NAS REGRAS QUE JA EXISTEM ─────────────────────
   *
   * A biblioteca ja guarda a FICHA e as PAGINAS em lugares separados, por decisao registrada em
   * `firestore.rules`: `biblioteca/{id}` e legivel por qualquer um, `biblioteca/{id}/paginas/{n}`
   * exige conta. Essa divisao nasceu de outro pedido — "a biblioteca aparece visivel para quem
   * quiser saber o que tem, mas para ler so logado".
   *
   * E ela E o preview que o T2.63 pede. Quando ha `instituicaoId`, a ficha continua publica (o
   * preview) e as paginas passam a exigir VINCULO ATIVO naquela instituicao. Nao precisou de
   * estrutura nova: precisou de uma condicao na regra que le as paginas.
   */
  instituicaoId?: string;
}

export interface Autor {
  id: string;
  nome: string;
  turma?: string;
  instituicao?: string;
}

export interface DocumentoHQ {
  formato: typeof FORMATO_DOCUMENTO;
  versao: typeof VERSAO_DOCUMENTO;
  id: string;
  titulo: string;
  criadoEm: string;
  atualizadoEm: string;
  autor: Autor;
  publicacao: Publicacao;
  paginas: Pagina[];
}

/** Rótulo em português para cada categoria, para a tela não inventar o texto. */
export const ROTULO_CATEGORIA: Record<Categoria, string> = {
  didatico: "Material didático",
  narrativa: "História narrativa",
  livre: "Criação livre",
};

/**
 * UM QUADRO DE UMA GRADE PRONTA, em FRAÇÕES da página (0 a 1) — nunca em pixels.
 *
 * Fração e não pixel porque o mesmo formato pode ser redimensionado depois, em
 * "Redimensionar": uma grade guardada em pixels descolaria da folha na primeira mudança.
 */
export interface QuadroDaGrade {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * A grade que a folha JÁ NASCE com ela.
 *
 * Isto existia como PROMESSA e não como comportamento. O seletor de formato dizia "Tirinha —
 * 3 quadros lado a lado" e "Página de revista — cabe de 4 a 6 quadros", e as duas abriam a
 * folha VAZIA: a única diferença entre os seis formatos era a dimensão do papel. A opção
 * "Grade livre — sem grade pronta" era a única honesta, e era idêntica às outras.
 *
 * Medido em produção em 10/09, com conta real: escolher A4 abre uma folha em branco, e
 * "Inserir Quadro" põe um quadrado de 150×150 sempre no MESMO canto — inserir quatro empilha
 * quatro iguais em (20,20).
 *
 * @param colunas    quantos quadros lado a lado
 * @param linhas     quantas fileiras
 * @param margem     a borda branca da folha, em fração
 * @param calha      o vão ENTRE quadros (o nome de tipografia para isso é calha)
 * @param alturaUtil quanto da folha a grade ocupa; < 1 deixa espaço embaixo (a legenda da
 *                   prancha didática)
 */
function grade(
  colunas: number,
  linhas: number,
  { margem = 0.05, calha = 0.025, alturaUtil = 1 } = {}
): QuadroDaGrade[] {
  const largura = (1 - 2 * margem - calha * (colunas - 1)) / colunas;
  const altura =
    (alturaUtil - 2 * margem - calha * (linhas - 1)) / linhas;

  const quadros: QuadroDaGrade[] = [];
  for (let linha = 0; linha < linhas; linha++) {
    for (let coluna = 0; coluna < colunas; coluna++) {
      quadros.push({
        x: margem + coluna * (largura + calha),
        y: margem + linha * (altura + calha),
        w: largura,
        h: altura,
      });
    }
  }
  return quadros;
}

export const PADROES_PAGINA: Record<
  FormatoPagina,
  {
    largura: number;
    altura: number;
    motor: MotorCanvas;
    rotulo: string;
    explicacao: string;
    /** Vazia de propósito em "grade-livre" e "capa" — nos dois o texto promete folha limpa. */
    grade: QuadroDaGrade[];
  }
> = {
  tirinha: {
    largura: 1200, altura: 400, motor: "grade-css",
    rotulo: "Tirinha — 3 quadros lado a lado",
    explicacao: "O formato de jornal. Curto, rápido de fazer, bom para uma piada ou uma cena.",
    // Os 3 lado a lado que o rótulo promete. Calha maior porque numa tirinha o vão entre
    // quadros é o que marca a passagem do tempo.
    grade: grade(3, 1, { margem: 0.035, calha: 0.03 }),
  },
  pagina: {
    largura: 1240, altura: 1754, motor: "konva",
    rotulo: "Página de revista — A4",
    explicacao: "O formato clássico de HQ, do tamanho de uma folha. Cabe de 4 a 6 quadros.",
    // 6 — o teto do que o próprio texto promete ("de 4 a 6"). Apagar sobra é mais fácil para
    // uma criança do que descobrir como inserir e onde posicionar.
    grade: grade(2, 3),
  },
  "grade-livre": {
    largura: 1240, altura: 1754, motor: "konva",
    rotulo: "Grade livre — você posiciona",
    explicacao: "Mesma folha, sem grade pronta: arraste os quadros para onde quiser.",
    // Vazia porque o texto diz "sem grade pronta". Era a única das seis que já cumpria o que
    // dizia, e continua sendo — agora por escolha, e não por acidente.
    grade: [],
  },
  webtoon: {
    largura: 800, altura: 4000, motor: "fluxo-vertical",
    rotulo: "Rolagem vertical — para celular",
    explicacao: "Uma tira alta que se lê rolando o dedo, um quadro por tela.",
    // 5 empilhados: "um quadro por tela", que é o que o rótulo promete.
    grade: grade(1, 5, { margem: 0.06, calha: 0.02 }),
  },
  capa: {
    largura: 1240, altura: 1754, motor: "svg",
    rotulo: "Capa — uma arte só",
    explicacao: "Sem quadros: a imagem grande com o título por cima.",
    // "Sem quadros", na letra.
    grade: [],
  },
  didatica: {
    largura: 1754, altura: 1240, motor: "grade-css",
    rotulo: "Prancha didática — deitada, com legenda",
    explicacao: "Folha deitada com espaço embaixo para a explicação do professor.",
    // `alturaUtil: 0.72` deixa os 28% de baixo livres — é o "espaço embaixo para a explicação
    // do professor" que o texto promete, e sem isso a grade cobriria a folha inteira.
    grade: grade(2, 2, { margem: 0.04, alturaUtil: 0.72 }),
  },
};
